/**
 * Renders `GameState` into the Three.js scene: placed tiles, meeples, legal-cell highlights, and
 * feature-completion glow. [P5-board]
 *
 * Deliberately imperative and event-driven rather than a full state diff — `AnimationDirector` [P6-anim]
 * calls straight into this on each `GameEvent`, which is what lets placement and scoring feel like
 * things that *happened* rather than a snapshot that changed.
 */

import {
  Box3,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Vector3,
} from 'three';
import type { Meeple, PlacedTile, Pos, PosKey, TileIndex } from '@carc/engine';
import { posKey } from '@carc/engine';
import type { Scene } from './scene.js';
import { createTileMesh, TILE_HEIGHT, TILE_SIZE, worldPosition, worldRotationY, type TileMesh } from './tile-mesh.js';
import { createMeepleMesh } from './meeple-mesh.js';
import { featureAnchor } from './geometry.js';

const meepleKey = (m: Pick<Meeple, 'pos' | 'featureIndex'>): string => `${posKey(m.pos)}#${m.featureIndex}`;

export class BoardView {
  private readonly tiles = new Map<PosKey, TileMesh>();
  private readonly meeples = new Map<string, Group>();
  private readonly legalCellsGroup = new Group();
  readonly root = new Group();

  constructor(
    private readonly scene: Scene,
    private readonly tileIndex: TileIndex,
  ) {
    this.root.name = 'board';
    this.root.add(this.legalCellsGroup);
    this.scene.three.add(this.root);
  }

  hasTile(pos: Pos): boolean {
    return this.tiles.has(posKey(pos));
  }

  getTileGroup(pos: Pos): Group | undefined {
    return this.tiles.get(posKey(pos))?.group;
  }

  worldPositionFor(pos: Pos): Vector3 {
    const [x, y, z] = worldPosition(pos);
    return new Vector3(x, y, z);
  }

  addTile(placed: PlacedTile): TileMesh {
    const def = this.tileIndex.get(placed.defId);
    if (!def) throw new Error(`Unknown tile def ${placed.defId}`);
    // Idempotent by position. The map would happily overwrite its entry while the old mesh stayed in
    // the group — a duplicate with no handle left to remove it by. Callers do guard with `hasTile`,
    // but that guard living only at the call sites is how this class of bug comes back.
    const existing = this.tiles.get(posKey(placed.pos));
    if (existing) existing.dispose();
    const mesh = createTileMesh(def, placed.drawIndex);
    const [x, y, z] = worldPosition(placed.pos);
    mesh.group.position.set(x, y, z);
    mesh.group.rotation.y = worldRotationY(placed.rotation);
    this.root.add(mesh.group);
    this.tiles.set(posKey(placed.pos), mesh);
    return mesh;
  }

  addMeeple(meeple: Meeple, colourHex: string): Group {
    const tileMesh = this.tiles.get(posKey(meeple.pos));
    const def = tileMesh ? this.tileIndex.get(tileMesh.defId) : undefined;
    // A farmer lies down; everyone else stands. See `meeple-mesh.ts`.
    const onFarm = def?.features[meeple.featureIndex]?.kind === 'field';
    const group = createMeepleMesh(colourHex, onFarm);
    if (tileMesh && def) {
      const anchor = featureAnchor(def, meeple.featureIndex);
      group.position.set(anchor.x * TILE_SIZE, TILE_HEIGHT, anchor.z * TILE_SIZE);
      tileMesh.group.add(group);
    }
    this.meeples.set(meepleKey(meeple), group);
    return group;
  }

  getMeepleGroup(meeple: Pick<Meeple, 'pos' | 'featureIndex'>): Group | undefined {
    return this.meeples.get(meepleKey(meeple));
  }

  removeMeeple(meeple: Pick<Meeple, 'pos' | 'featureIndex'>): Group | undefined {
    const key = meepleKey(meeple);
    const group = this.meeples.get(key);
    if (group) {
      group.parent?.remove(group);
      this.meeples.delete(key);
    }
    return group;
  }

  /**
   * Highlight where the held tile can go.
   *
   * TWO sets, deliberately. `fitsNow` are cells the tile drops into at its current rotation. `needsTurn`
   * are cells it would fit if you rotated it — shown dimmer, because previously they were not shown at
   * all, and a player would see two green squares, conclude those were the only options, and never
   * discover that turning the tile opened up half the board. Showing only the current rotation is
   * technically accurate and practically misleading.
   */
  showLegalCells(fitsNow: readonly Pos[], needsTurn: readonly Pos[] = []): void {
    while (this.legalCellsGroup.children.length > 0) {
      const child = this.legalCellsGroup.children[0] as Mesh;
      this.legalCellsGroup.remove(child);
      child.geometry.dispose();
      (child.material as MeshBasicMaterial).dispose();
    }

    const add = (pos: Pos, colour: string, opacity: number, scale: number): void => {
      const marker = new Mesh(
        new PlaneGeometry(TILE_SIZE * scale, TILE_SIZE * scale),
        new MeshBasicMaterial({ color: colour, transparent: true, opacity, depthWrite: false }),
      );
      marker.rotation.x = -Math.PI / 2;
      const [x, y, z] = worldPosition(pos);
      marker.position.set(x, y + 0.003, z);
      this.legalCellsGroup.add(marker);
    };

    const nowKeys = new Set(fitsNow.map(posKey));
    // Amber: "possible, but turn the tile first."
    for (const pos of needsTurn) {
      if (!nowKeys.has(posKey(pos))) add(pos, '#c98a2e', 0.34, 0.74);
    }
    // Green, full size: "drop it here."
    for (const pos of fitsNow) add(pos, '#3fa34d', 0.5, 0.9);
  }

  clearLegalCells(): void {
    this.showLegalCells([]);
  }

  setTileHighlight(pos: Pos, on: boolean, valid: boolean): void {
    this.tiles.get(posKey(pos))?.setHighlight(on, valid);
  }

  /** Pulses the tops of every tile in a completed feature. Used for [P6-anim]'s feature-completion glow. */
  flashFeature(positions: readonly Pos[], durationMs: number, onDone?: () => void): void {
    const meshes = positions.map((p) => this.tiles.get(posKey(p))).filter((m): m is TileMesh => Boolean(m));
    for (const m of meshes) m.setHighlight(true, true);
    window.setTimeout(() => {
      for (const m of meshes) m.setHighlight(false, true);
      onDone?.();
    }, durationMs);
  }

  /**
   * Detach this board from the scene and let go of its GPU resources.
   *
   * Without it, starting a second session leaves the previous board's group in the scene — every tile
   * still drawn, at the same coordinates, under the new one. That is one half of the "duplicate tile"
   * report; the other half is a second `PlacementController` still listening to the same canvas.
   */
  dispose(): void {
    this.clearLegalCells();
    for (const mesh of this.tiles.values()) mesh.dispose();
    for (const meeple of this.meeples.values()) meeple.removeFromParent();
    this.tiles.clear();
    this.meeples.clear();
    this.root.removeFromParent();
  }

  boardBounds(): Box3 {
    const box = new Box3();
    if (this.tiles.size === 0) {
      box.setFromCenterAndSize(new Vector3(0, 0, 0), new Vector3(6, 1, 6));
      return box;
    }
    for (const mesh of this.tiles.values()) {
      box.expandByPoint(mesh.group.position);
    }
    box.expandByScalar(TILE_SIZE * 1.5);
    return box;
  }
}
